import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Phone, Mail, Building2, Search, Users, Plus, Trash2, Edit2,
  User, Briefcase, Shield, ExternalLink, AlertCircle, Loader2,
  UserCircle, PhoneCall, Globe, Filter
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryConfig = {
  external: { label: 'Externe', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: ExternalLink },
  emergency: { label: 'Urgence', color: 'bg-red-100 text-red-700 border-red-200', icon: AlertCircle },
  service: { label: 'Service', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: Briefcase },
  other: { label: 'Autre', color: 'bg-slate-100 text-slate-600 border-slate-200', icon: Globe }
};

export default function DirectoryPage() {
  const { isAdmin, token } = useAuth();
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [form, setForm] = useState({
    name: '',
    position: '',
    department: '',
    email: '',
    phone: '',
    category: 'external'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, contactsRes] = await Promise.all([
        axios.get(`${API_URL}/api/users/directory`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/contacts`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setUsers(usersRes.data);
      setContacts(contactsRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrUpdate = async (e) => {
    e.preventDefault();
    if (!form.name) {
      toast.error('Le nom est obligatoire');
      return;
    }
    setSubmitting(true);
    try {
      if (editingContact) {
        await axios.put(`${API_URL}/api/contacts/${editingContact.id}`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Contact modifié');
      } else {
        await axios.post(`${API_URL}/api/contacts`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Contact ajouté');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!contactToDelete) return;
    try {
      await axios.delete(`${API_URL}/api/contacts/${contactToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contact supprimé');
      setDeleteConfirmOpen(false);
      setContactToDelete(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const openEditDialog = (contact) => {
    setEditingContact(contact);
    setForm({
      name: contact.name || '',
      position: contact.position || '',
      department: contact.department || '',
      email: contact.email || '',
      phone: contact.phone || '',
      category: contact.category || 'external'
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setForm({ name: '', position: '', department: '', email: '', phone: '', category: 'external' });
    setEditingContact(null);
  };

  // Combine and filter
  const allEntries = [
    ...users.map(u => ({ 
      ...u, 
      name: `${u.first_name || ''} ${u.last_name || ''}`.trim(),
      is_user: true 
    })),
    ...contacts.map(c => ({ ...c, is_contact: true }))
  ];

  const filteredEntries = allEntries.filter(entry => {
    const searchStr = `${entry.name} ${entry.email || ''} ${entry.department || ''} ${entry.position || ''} ${entry.phone || ''}`.toLowerCase();
    const matchesSearch = searchStr.includes(search.toLowerCase());
    
    if (activeTab === 'all') return matchesSearch;
    if (activeTab === 'employees') return matchesSearch && entry.is_user;
    if (activeTab === 'contacts') return matchesSearch && entry.is_contact;
    return matchesSearch;
  });

  const stats = {
    total: allEntries.length,
    employees: users.length,
    contacts: contacts.length,
    departments: [...new Set(allEntries.map(e => e.department).filter(Boolean))].length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="directory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Annuaire</h1>
          <p className="text-slate-500 mt-1">Trouvez les coordonnées de vos collègues et contacts</p>
        </div>
        {isAdmin && (
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />Ajouter un contact
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500 hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveTab('all')}>
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <p className="text-4xl font-bold text-slate-800">{stats.total}</p>
            </div>
            <p className="text-sm text-slate-500 mt-3">Total contacts</p>
          </CardContent>
        </Card>
        <Card className={`border-l-4 border-l-emerald-500 hover:shadow-md transition-shadow cursor-pointer ${activeTab === 'employees' ? 'ring-2 ring-emerald-200' : ''}`} onClick={() => setActiveTab('employees')}>
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-xl">
                <UserCircle className="w-6 h-6 text-emerald-600" />
              </div>
              <p className="text-4xl font-bold text-slate-800">{stats.employees}</p>
            </div>
            <p className="text-sm text-slate-500 mt-3">Employés</p>
          </CardContent>
        </Card>
        <Card className={`border-l-4 border-l-amber-500 hover:shadow-md transition-shadow cursor-pointer ${activeTab === 'contacts' ? 'ring-2 ring-amber-200' : ''}`} onClick={() => setActiveTab('contacts')}>
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-xl">
                <PhoneCall className="w-6 h-6 text-amber-600" />
              </div>
              <p className="text-4xl font-bold text-slate-800">{stats.contacts}</p>
            </div>
            <p className="text-sm text-slate-500 mt-3">Contacts externes</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500 hover:shadow-md transition-shadow">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-xl">
                <Building2 className="w-6 h-6 text-purple-600" />
              </div>
              <p className="text-4xl font-bold text-slate-800">{stats.departments}</p>
            </div>
            <p className="text-sm text-slate-500 mt-3">Départements</p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Tabs */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-center">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher par nom, email, département, téléphone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full sm:w-auto">
              <TabsList>
                <TabsTrigger value="all">Tous</TabsTrigger>
                <TabsTrigger value="employees">Employés</TabsTrigger>
                <TabsTrigger value="contacts">Contacts</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {filteredEntries.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-700 mb-1">Aucun résultat</h3>
            <p className="text-slate-500">Essayez de modifier vos critères de recherche</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left py-4 px-6 text-sm font-semibold text-slate-600">Nom</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-slate-600">Fonction</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-slate-600">Département</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-slate-600">Email</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-slate-600">Téléphone</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-slate-600">Type</th>
                  {isAdmin && <th className="text-left py-4 px-4 text-sm font-semibold text-slate-600">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredEntries.map((entry, idx) => {
                  const initials = entry.name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() || '?';
                  const CategoryIcon = entry.is_contact ? (categoryConfig[entry.category]?.icon || Globe) : Shield;
                  
                  return (
                    <tr key={entry.id || idx} className="hover:bg-slate-50 transition-colors">
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarFallback className={entry.is_user ? 'bg-slate-800 text-white' : 'bg-amber-100 text-amber-700'}>
                              {initials}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-semibold text-slate-800">{entry.name}</p>
                            {entry.is_contact && entry.category && (
                              <Badge className={`${categoryConfig[entry.category]?.color} border text-xs mt-1`}>
                                {categoryConfig[entry.category]?.label}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-slate-600">{entry.position || '-'}</span>
                      </td>
                      <td className="py-4 px-4">
                        {entry.department ? (
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-slate-400" />
                            <span className="text-slate-600">{entry.department}</span>
                          </div>
                        ) : '-'}
                      </td>
                      <td className="py-4 px-4">
                        {entry.email ? (
                          <a href={`mailto:${entry.email}`} className="flex items-center gap-2 text-blue-600 hover:text-blue-700">
                            <Mail className="w-4 h-4" />
                            <span className="truncate max-w-[200px]">{entry.email}</span>
                          </a>
                        ) : '-'}
                      </td>
                      <td className="py-4 px-4">
                        {entry.phone ? (
                          <a href={`tel:${entry.phone}`} className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700">
                            <Phone className="w-4 h-4" />
                            <span>{entry.phone}</span>
                          </a>
                        ) : '-'}
                      </td>
                      <td className="py-4 px-4">
                        <Badge variant={entry.is_user ? 'default' : 'secondary'} className={entry.is_user ? 'bg-slate-800' : ''}>
                          {entry.is_user ? 'Employé' : 'Contact'}
                        </Badge>
                      </td>
                      {isAdmin && (
                        <td className="py-4 px-4">
                          {entry.is_contact && (
                            <div className="flex items-center gap-2">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => openEditDialog(entry)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => { setContactToDelete(entry); setDeleteConfirmOpen(true); }}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              {editingContact ? 'Modifier le contact' : 'Ajouter un contact'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateOrUpdate} className="space-y-4">
            <div className="space-y-2">
              <Label>Nom <span className="text-red-500">*</span></Label>
              <Input 
                value={form.name} 
                onChange={(e) => setForm({ ...form, name: e.target.value })} 
                placeholder="Nom complet" 
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fonction</Label>
                <Input 
                  value={form.position} 
                  onChange={(e) => setForm({ ...form, position: e.target.value })} 
                  placeholder="Ex: Directeur" 
                />
              </div>
              <div className="space-y-2">
                <Label>Département</Label>
                <Input 
                  value={form.department} 
                  onChange={(e) => setForm({ ...form, department: e.target.value })} 
                  placeholder="Ex: Marketing" 
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input 
                type="email"
                value={form.email} 
                onChange={(e) => setForm({ ...form, email: e.target.value })} 
                placeholder="email@exemple.com" 
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input 
                  value={form.phone} 
                  onChange={(e) => setForm({ ...form, phone: e.target.value })} 
                  placeholder="+33 1 23 45 67 89" 
                />
              </div>
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="external">Externe</SelectItem>
                    <SelectItem value="emergency">Urgence</SelectItem>
                    <SelectItem value="service">Service</SelectItem>
                    <SelectItem value="other">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingContact ? 'Enregistrer' : 'Ajouter'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Supprimer le contact
            </DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            Êtes-vous sûr de vouloir supprimer <strong>{contactToDelete?.name}</strong> ? Cette action est irréversible.
          </p>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Annuler
            </Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={handleDelete}>
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
